/**
 * HomeHowItWorksSection — homepage competitor comparison (#comparison).
 * Replaces legacy ComparisonCards; left preview + right steps/CTA.
 * 5 steps (resume tools → AI tools → job boards → prep apps → GoGrabJob).
 * Fixed-height card, no scrollbars in the preview panel.
 */

import { motion, AnimatePresence } from 'framer-motion';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight,
  Check,
  LayoutDashboard,
  ListChecks,
  Sparkles,
  Search,
  X,
} from 'lucide-react';

import { trackCTA } from '../../../utils/metaPixel';
import {
  useCompetitorCompareSteps,
  useCompetitorCompareLabels,
  useCompetitorGgjStrengths,
  type CompetitorStep,
} from './useCompetitorCompareSteps';

// ── Types ──────────────────────────────────────────────────────────────────────

type Bullet = { bold: string; rest: string };
type Row    = { feature: string; them: boolean; us: boolean };

type Step = CompetitorStep;

// ── Per-step accent palettes (light/dark handled inline) ───────────────────────

type Palette = {
  label: string;   // dark text on accent
  pill: string;    // accent foreground
  cardBg: string;  // GoGrabJob card bg
  cardBorder: string;
};

const STEP_DWELL_MS = 14000;

interface HomeHowItWorksSectionProps {
  onNavigate?: (view: 'home' | 'login' | 'signup' | 'contact-us' | 'optimize') => void;
}

/** Warm sand palette — aligned with section mesh */
const PALETTES: Palette[] = [
  { label: '#6b5c4f', pill: '#a68b5b', cardBg: '#faf7f2', cardBorder: '#e8e0d4' },
  { label: '#6b5c4f', pill: '#a68b5b', cardBg: '#faf7f2', cardBorder: '#e8e0d4' },
  { label: '#6b5c4f', pill: '#a68b5b', cardBg: '#faf7f2', cardBorder: '#e8e0d4' },
  { label: '#6b5c4f', pill: '#a68b5b', cardBg: '#faf7f2', cardBorder: '#e8e0d4' },
  { label: '#6b5c4f', pill: '#a68b5b', cardBg: '#f5f0e8', cardBorder: '#ddd0bc' },
];

// ── Helpers ────────────────────────────────────────────────────────────────────

function hasScrollableRoom(
  target: EventTarget | null,
  boundary: HTMLElement,
  deltaY: number,
): boolean {
  let el = target as HTMLElement | null;
  while (el && el !== boundary) {
    const oy = window.getComputedStyle(el).overflowY;
    if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight + 2) {
      if (deltaY > 0 && el.scrollTop + el.clientHeight < el.scrollHeight - 2) return true;
      if (deltaY < 0 && el.scrollTop > 2) return true;
    }
    el = el.parentElement;
  }
  return false;
}

// ── Root ───────────────────────────────────────────────────────────────────────

const HomeHowItWorksSection: React.FC<HomeHowItWorksSectionProps> = ({ onNavigate }) => {
  const { t } = useTranslation();
  const STEPS = useCompetitorCompareSteps();
  const labels = useCompetitorCompareLabels();
  const layoutRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<number | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const stepRef   = useRef(0);
  const accumRef  = useRef(0);
  const touchY    = useRef(0);
  const maxIndex  = STEPS.length - 1;

  const goTo = useCallback((i: number) => {
    const clamped = Math.max(0, Math.min(i, maxIndex));
    stepRef.current = clamped;
    accumRef.current = 0;
    setActiveStep(clamped);
  }, [maxIndex]);

  const scheduleAdvance = useCallback(
    (index: number) => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => {
        goTo((index + 1) % STEPS.length);
      }, STEP_DWELL_MS);
    },
    [goTo, STEPS.length],
  );

  useEffect(() => {
    scheduleAdvance(activeStep);
    return () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    };
  }, [activeStep, scheduleAdvance]);

  const handleSelect = useCallback(
    (index: number) => {
      goTo(index);
    },
    [goTo],
  );

  useEffect(() => {
    const card = layoutRef.current;
    if (!card) return;
    const THRESHOLD = 44;

    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
      if (hasScrollableRoom(e.target, card, e.deltaY)) return;
      const step = stepRef.current;
      if (e.deltaY < 0 && step === 0) return;
      if (e.deltaY > 0 && step === maxIndex) return;
      e.preventDefault();
      accumRef.current += e.deltaY;
      if (accumRef.current >= THRESHOLD) {
        const next = Math.min(step + Math.floor(accumRef.current / THRESHOLD), maxIndex);
        accumRef.current %= THRESHOLD;
        stepRef.current = next;
        setActiveStep(next);
      } else if (accumRef.current <= -THRESHOLD) {
        const prev = Math.max(step + Math.ceil(accumRef.current / THRESHOLD), 0);
        accumRef.current %= THRESHOLD;
        stepRef.current = prev;
        setActiveStep(prev);
      }
    };

    const onMouseLeave = () => { accumRef.current = 0; };

    const onTouchStart = (e: TouchEvent) => { touchY.current = e.touches[0].clientY; };

    const onTouchMove = (e: TouchEvent) => {
      const dy = touchY.current - e.touches[0].clientY;
      if (hasScrollableRoom(e.target, card, dy)) return;
      if (dy < 0 && stepRef.current === 0) return;
      if (dy > 0 && stepRef.current === maxIndex) return;
      if (Math.abs(dy) >= 8) e.preventDefault();
    };

    const onTouchEnd = (e: TouchEvent) => {
      const dy = touchY.current - (e.changedTouches[0]?.clientY ?? touchY.current);
      if (Math.abs(dy) < 40) return;
      if (dy > 0) goTo(stepRef.current + 1);
      else goTo(stepRef.current - 1);
    };

    card.addEventListener('wheel',      onWheel,      { passive: false });
    card.addEventListener('touchstart', onTouchStart, { passive: true  });
    card.addEventListener('touchmove',  onTouchMove,  { passive: false });
    card.addEventListener('touchend',   onTouchEnd,   { passive: true  });
    card.addEventListener('mouseleave', onMouseLeave, { passive: true  });

    return () => {
      card.removeEventListener('wheel',      onWheel);
      card.removeEventListener('touchstart', onTouchStart);
      card.removeEventListener('touchmove',  onTouchMove);
      card.removeEventListener('touchend',   onTouchEnd);
      card.removeEventListener('mouseleave', onMouseLeave);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const step = STEPS[activeStep];

  return (
    <section
      id="comparison"
      aria-labelledby="home-comparison-title"
      className="home-how-it-works-section home-comparison-section scroll-mt-28 py-14 md:py-20"
    >

      <div className="w-full px-4 sm:px-8 md:px-10 lg:px-12">
        <div ref={layoutRef} className="home-how-it-works-layout mx-auto w-full max-w-7xl">
          <header className="hiw__header">
            <p className="hiw__eyebrow">{t('home.howItWorksFlow.eyebrow')}</p>
            <h2 id="home-comparison-title" className="hiw__title text-balance">
              {t('home.howItWorksFlow.titleBefore', { defaultValue: 'Everyone else stops at one step.' })}{' '}
              <span className="hiw__title-accent">
                {t('home.howItWorksFlow.titleAccent', { defaultValue: 'We own the full loop.' })}
              </span>
            </h2>
            <p className="hiw__subtitle text-balance">
              {t('home.howItWorksFlow.subtitle', {
                defaultValue:
                  'Compare resume tools, AI, job boards, and prep apps—and see what GoGrabJob adds in one workspace.',
              })}
            </p>
          </header>

          {/* Left — larger preview panel (do not use hfs__left/hfs__visual-wrap grid classes here) */}
          <div
            className="hiw__visual-wrap hfs__visual-wrap w-full min-w-0"
            aria-live="polite"
            aria-label={`Comparison preview: ${step.title} ${step.titleAccent}`}
          >
            <ComparisonPreviewPanel step={step} stepIndex={activeStep} />
          </div>

          {/* Right — mirrors Features left panel (copy, CTA, dash carousel, step cards) */}
          <div className="hiw__steps-col hiw__panel-right min-w-0 w-full">
            <div className="hiw__panel-intro-wrap">
              <p className="hiw__panel-intro">
                {t('home.howItWorksFlow.lead', {
                  defaultValue: 'Select a category. The preview shows what others offer—and what GoGrabJob adds.',
                })}
              </p>
              <button
                type="button"
                className="landing-agent-inline__cta home-comparison-section__cta"
                onClick={() => {
                  trackCTA('home_comparison_get_started');
                  onNavigate?.('signup');
                }}
              >
                {t('home.comparison.ctaTryFree', { defaultValue: 'Try GoGrabJob Free' })}
                <ArrowRight className="h-3.5 w-3.5 shrink-0" aria-hidden />
              </button>
            </div>

            <div className="hfs__nav">
              <div className="hfs__dashes" aria-hidden>
                {STEPS.map((s, i) => (
                  <span
                    key={s.id}
                    className={i === activeStep ? 'hfs__dash hfs__dash--active' : 'hfs__dash'}
                  />
                ))}
              </div>

              <div className="hfs__list" role="tablist" aria-label={t('home.howItWorksFlow.eyebrow')}>
                {STEPS.map((step, i) => {
                  const isActive = i === activeStep;
                  const palette = PALETTES[i];
                  const gapLine = step.bullets[0]
                    ? `${step.bullets[0].bold} ${step.bullets[0].rest}`
                    : step.desc;

                  return (
                    <button
                      key={step.id}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      className={`hfs__card${isActive ? ' hfs__card--active' : ''}`}
                      onClick={() => handleSelect(i)}
                    >
                      <div className="hfs__card-body">
                        <span className="hfs__card-title">
                          {step.title}{' '}
                          <span
                            className={`hiw__step-accent${isActive ? ' hiw__step-accent--active' : ''}`}
                            style={isActive ? { color: palette.pill } : undefined}
                          >
                            {step.titleAccent}
                          </span>
                        </span>
                        <AnimatePresence initial={false}>
                          {isActive && (
                            <motion.div
                              key={`${step.id}-detail`}
                              initial={{ opacity: 0, height: 0, marginTop: 0 }}
                              animate={{ opacity: 1, height: 'auto', marginTop: '0.625rem' }}
                              exit={{ opacity: 0, height: 0, marginTop: 0 }}
                              transition={{ duration: 0.28, ease: 'easeOut' }}
                              className="hiw__step-detail"
                            >
                              <p className="hiw__step-detail-kicker">{labels.whereOthersFallShort}</p>
                              <p className="hfs__card-desc hiw__step-detail-gap">{gapLine}</p>
                              <p className="hiw__step-detail-kicker hiw__step-detail-kicker--ggj">
                                {labels.whatGgjGivesYou}
                              </p>
                              <p className="hiw__step-detail-value">{step.ggjPitch}</p>
                              <p className="hiw__step-detail-hint">{labels.seeComparison}</p>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomeHowItWorksSection;

// ── Left visual: same shell as HomeFeaturesSection (mesh outer + framed preview) ──

function ComparisonPreviewPanel({ step, stepIndex }: { step: Step; stepIndex: number }) {
  const labels = useCompetitorCompareLabels();
  const palette = PALETTES[stepIndex] ?? PALETTES[0];

  return (
    <div className="hfs__visual">
      <div className="hfs__mesh hiw-mesh-layer" aria-hidden />

      <div className="hfs__preview-context">
        <AnimatePresence mode="wait">
          <motion.div
            key={step.id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18, ease: 'easeInOut' }}
            className="hfs__frame"
          >
            <div className="hfs-chrome">
              <div className="hfs-chrome__header">
                <span
                  className="hfs-chrome__header-icon"
                  style={{ background: `linear-gradient(135deg, ${palette.pill}, ${palette.label})` }}
                  aria-hidden
                >
                  {step.icon}
                </span>
                <span className="hfs-chrome__header-title">
                  {step.title}{' '}
                  <strong>{step.titleAccent}</strong>
                </span>
                <span className="hfs-chrome__header-badge hiw-preview-badge">{labels.vsOthers}</span>
              </div>

              <div className="hfs-chrome__body hiw-preview-chrome__body">
                <div
                  className={`hfs-preview hfs-preview--fill hiw-preview-inner${
                    step.id === 'full-loop' ? ' hiw-preview-inner--full-loop' : ''
                  }`}
                >
                  {step.id === 'full-loop' ? (
                    <FullLoopPreview palette={palette} />
                  ) : (
                    <StandardPreview step={step} palette={palette} />
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

// ── Standard preview: 2-col cards + feature table ─────────────────────────────

function StandardPreview({ step, palette }: { step: Step; palette: Palette }) {
  const labels = useCompetitorCompareLabels();
  const ggjStrengths = useCompetitorGgjStrengths();
  const strengths = ggjStrengths[step.id] ?? [step.ggjPitch];
  const advantageCount = ggjAdvantageCount(step);

  return (
    <>
      <div className="hiw-preview-compare-bar shrink-0">
        <span className="hiw-preview-compare-bar__label">{labels.comparing}</span>
        <span className="hiw-preview-compare-bar__them">{step.competitor}</span>
        <span className="hiw-preview-compare-bar__vs">{labels.vs}</span>
        <span className="hiw-preview-compare-bar__us">{labels.ggj}</span>
      </div>

      <p className="hiw-preview-summary shrink-0">{labels.addsCapabilities(advantageCount)}</p>

      <div className="grid min-h-[10.5rem] shrink-0 grid-cols-2 gap-2.5">
        <div className="hiw-preview-card hiw-preview-card--them flex flex-col overflow-hidden">
          <div className="hiw-preview-card__head">
            <span className="hiw-preview-card__title">{labels.whatOthersOffer}</span>
            <span className="hiw-preview-card__tag hiw-preview-card__tag--gap">{labels.limited}</span>
          </div>
          <div className="hiw-preview-card__body">
            <p className="hiw-preview-card__vendor">{step.competitor}</p>
            <ul className="hiw-preview-card__list">
              {step.bullets.map((b) => (
                <li key={b.bold} className="hiw-preview-card__list-item">
                  <X className="hiw-preview-card__icon hiw-preview-card__icon--x" aria-hidden />
                  <span>
                    <strong>{b.bold}</strong> {b.rest}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div
          className="hiw-preview-card hiw-preview-card--us flex flex-col overflow-hidden"
          style={{
            borderColor: palette.cardBorder,
            backgroundColor: palette.cardBg,
          }}
        >
          <div className="hiw-preview-card__head" style={{ borderColor: palette.cardBorder }}>
            <span className="hiw-preview-card__title" style={{ color: palette.label }}>
              {labels.whatGgjOffers}
            </span>
            <span className="hiw-preview-card__tag hiw-preview-card__tag--win">
              {labels.youGetThis}
            </span>
          </div>
          <div className="hiw-preview-card__body">
            <p className="hiw-preview-card__pitch">{step.ggjPitch}</p>
            <ul className="hiw-preview-card__list">
              {strengths.map((s) => (
                <li key={s} className="hiw-preview-card__list-item">
                  <span
                    className="hiw-preview-card__icon hiw-preview-card__icon--check"
                    aria-hidden
                  >
                    <Check className="h-2.5 w-2.5 text-white" />
                  </span>
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="hiw-preview-table shrink-0 overflow-hidden rounded-xl">
        <div className="hiw-preview-table__head">
          <div>{labels.capability}</div>
          <div className="text-center">{labels.othersCol}</div>
          <div className="text-center hiw-preview-table__ggj">{labels.ggjCol}</div>
        </div>
        <div className="hiw-preview-table__body divide-y divide-[#f0ebe3]">
          {step.rows.map((row) => {
            const isAdvantage = row.us && !row.them;
            return (
              <div
                key={row.feature}
                className={`hiw-preview-table__row${isAdvantage ? ' hiw-preview-table__row--advantage' : ''}`}
              >
                <div className="hiw-preview-table__feature">{row.feature}</div>
                <div className="flex justify-center">
                  {row.them ? (
                    <Check className="h-3.5 w-3.5 text-stone-400" aria-label="Others have this" />
                  ) : (
                    <X className="h-3.5 w-3.5 text-stone-400" aria-label="Others lack this" />
                  )}
                </div>
                <div className="flex justify-center hiw-preview-table__ggj-cell">
                  <Check className="h-3.5 w-3.5 marketing-accent-text" aria-label="GoGrabJob has this" />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

function ggjAdvantageCount(step: Step): number {
  return step.rows.filter((r) => r.us && !r.them).length;
}

// ── Full-loop preview (step 5): scorecard matrix ───────────────────────────────

const SCORECARD_COLS = ['Discover', 'Optimize', 'Track', 'Prep'] as const;
const SCORECARD_ROWS: { name: string; scores: [boolean, boolean, boolean, boolean] }[] = [
  { name: 'Resume builders',   scores: [false, true,  false, false] },
  { name: 'AI writing tools',  scores: [false, true,  false, false] },
  { name: 'Job boards',        scores: [true,  false, false, false] },
  { name: 'Prep apps',         scores: [false, false, false, true ] },
  { name: 'GoGrabJob ★',       scores: [true,  true,  true,  true ] },
];

const LOOP_PIPELINE = [
  { label: 'Discover', desc: 'Ranked matches across boards', Icon: Search },
  { label: 'Optimize', desc: 'ATS score + JD-tailored resume', Icon: Sparkles },
  { label: 'Track', desc: 'Apply, status & follow-ups', Icon: LayoutDashboard },
  { label: 'Prep', desc: 'Role-specific interview prep', Icon: ListChecks },
] as const;

const SLICE_ONLY = [
  { tool: 'Resume builders', slice: 'Optimize', gap: 'No discover, track, or prep' },
  { tool: 'AI writing tools', slice: 'Optimize', gap: 'Draft vanishes — no pipeline' },
  { tool: 'Job boards', slice: 'Discover', gap: 'No optimize, track, or prep' },
  { tool: 'Prep apps', slice: 'Prep', gap: 'Not tied to your applications' },
] as const;

function FullLoopPreview({ palette }: { palette: Palette }) {
  const STEPS = useCompetitorCompareSteps();
  const labels = useCompetitorCompareLabels();
  const isGGJRow = (name: string) => name.startsWith('GoGrabJob');
  const step = STEPS[STEPS.length - 1];

  return (
    <>
      <div className="hiw-preview-compare-bar shrink-0">
        <span className="hiw-preview-compare-bar__label">{labels.comparing}</span>
        <span className="hiw-preview-compare-bar__them">{labels.fullLoopCompareThem}</span>
        <span className="hiw-preview-compare-bar__vs">{labels.vs}</span>
        <span className="hiw-preview-compare-bar__us">{labels.fullLoopCompareUs}</span>
      </div>

      <p className="hiw-preview-summary shrink-0">{step.ggjPitch}</p>

      <div
        className="hiw-full-loop-callout shrink-0 rounded-lg border p-2"
        style={{ borderColor: palette.cardBorder, backgroundColor: palette.cardBg }}
      >
        <p className="text-[10px] font-semibold leading-snug text-stone-700">{labels.fullLoopCallout}</p>
      </div>

      <div className="hiw-full-loop-scorecard shrink-0 overflow-hidden rounded-xl border border-[#e8e0d4] bg-white">
        <div
          className="hiw-full-loop-scorecard__head grid shrink-0 border-b border-[#ebe4d8] bg-[#f5f0e8]/90"
          style={{ gridTemplateColumns: '1fr repeat(4, 2.25rem)' }}
        >
          <div className="px-2 py-1.5 text-[9px] font-bold uppercase tracking-wide text-stone-500">Tool</div>
          {SCORECARD_COLS.map((col) => (
            <div key={col} className="px-0.5 py-1.5 text-center text-[8px] font-bold text-stone-500">
              {col}
            </div>
          ))}
        </div>
        <div className="hiw-full-loop-scorecard__body divide-y divide-[#f0ebe3]">
          {SCORECARD_ROWS.map((row) => {
            const isUs = isGGJRow(row.name);
            return (
              <div
                key={row.name}
                className={`grid items-center${isUs ? ' hiw-full-loop-scorecard__row--ggj' : ''}`}
                style={{ gridTemplateColumns: '1fr repeat(4, 2.25rem)' }}
              >
                <div
                  className={`px-2 py-1 text-[10px] font-semibold leading-tight${isUs ? ' marketing-accent-text' : ''}`}
                >
                  {row.name}
                </div>
                {row.scores.map((ok, ci) => (
                  <div key={ci} className="flex items-center justify-center py-1">
                    {ok ? (
                      <Check className={`h-3 w-3${isUs ? ' marketing-accent-text' : ''}`} style={isUs ? undefined : { color: '#94a3b8' }} />
                    ) : (
                      <X className="h-3 w-3 text-stone-300 hiw-full-loop-scorecard__miss" aria-hidden />
                    )}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>

      <div className="hiw-full-loop-pipeline shrink-0">
        <p className="hiw-full-loop-section-label">The full loop in one workspace</p>
        <div className="hiw-full-loop-pipeline__track">
          {LOOP_PIPELINE.map((stage, i) => (
            <React.Fragment key={stage.label}>
              <div className="hiw-full-loop-pipeline__step">
                <span className="hiw-full-loop-pipeline__icon" aria-hidden>
                  <stage.Icon className="h-3 w-3" />
                </span>
                <span className="hiw-full-loop-pipeline__label">{stage.label}</span>
                <span className="hiw-full-loop-pipeline__desc">{stage.desc}</span>
              </div>
              {i < LOOP_PIPELINE.length - 1 && (
                <ArrowRight className="hiw-full-loop-pipeline__arrow h-3 w-3 shrink-0" aria-hidden />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="hiw-full-loop-slices shrink-0">
        <p className="hiw-full-loop-section-label">Others own one slice each</p>
        <div className="hiw-full-loop-slices__grid">
          {SLICE_ONLY.map((item) => (
            <div key={item.tool} className="hiw-full-loop-slice-card">
              <span className="hiw-full-loop-slice-card__tool">{item.tool}</span>
              <span className="hiw-full-loop-slice-card__only">
                Only <strong>{item.slice}</strong>
              </span>
              <span className="hiw-full-loop-slice-card__gap">{item.gap}</span>
            </div>
          ))}
        </div>
      </div>

      <div
        className="hiw-full-loop-wins shrink-0 rounded-lg border p-2.5"
        style={{
          borderColor: palette.cardBorder,
          backgroundColor: 'rgba(95, 133, 104, 0.08)',
        }}
      >
        <p className="hiw-full-loop-section-label hiw-full-loop-section-label--ggj">Why the full loop matters</p>
        <ul className="hiw-full-loop-wins__list">
          {step.bullets.map((b) => (
            <li key={b.bold} className="hiw-full-loop-wins__item">
              <Check className="h-3.5 w-3.5 shrink-0 marketing-accent-text" aria-hidden />
              <span>
                <strong>{b.bold}</strong> {b.rest}
              </span>
            </li>
          ))}
        </ul>
        <p className="hiw-full-loop-wins__footer">
          <strong>4 categories above</strong> → one agent, one history, zero tab-hopping.
        </p>
      </div>
    </>
  );
}
